import { Link } from 'react-router-dom';
import { useState } from 'react';

import axios from 'axios';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/useAuth';

import { givingApi, type Preview, type Receipt, type ImportRow } from '@/api/giving';

import { clearFinancialState } from './accessState';

import { useTreasure, useFinancialErrors } from './treasureAccess';

const fields = ['transaction_id', 'allocation_id', 'date', 'amount', 'currency', 'fund', 'external_donor_id', 'kind', 'original_id'];

export function ImportWorkspace() {

    const { churchId } = useAuth();

    const client = useQueryClient();

    const api = givingApi(churchId ?? '');

    const { access, userId } = useTreasure();

    const setup = useQuery({ queryKey: ['giving', churchId, 'setup', userId], queryFn: api.setup, enabled: !!access.enabled, gcTime: 0, staleTime: 0 });

    useFinancialErrors(setup.error);

    const [busy, setBusy] = useState(false), [message, setMessage] = useState('');

    const [sourceId, setSourceId] = useState(''), [file, setFile] = useState<File | null>(null);

    const [mapping, setMapping] = useState<Record<string, string>>({ date: 'date', amount: 'amount', currency: 'currency', fund: 'fund' });

    const [preview, setPreview] = useState<Preview | null>(null), [receipt, setReceipt] = useState<Receipt | null>(null);

    const [resumeId, setResumeId] = useState(''), [coverage, setCoverage] = useState('UNKNOWN'), [start, setStart] = useState(''), [end, setEnd] = useState('');

    const [dirty, setDirty] = useState(false);

    const [previewPage, setPreviewPage] = useState(0);

    async function run(work: () => Promise<unknown>) {

        setBusy(true);

        setMessage('');

        try {

            await work();

            await client.invalidateQueries({ queryKey: ['giving', churchId] });

            setMessage('Saved.');

        }

        catch (error) {

            const status = axios.isAxiosError(error) ? error.response?.status : undefined;

            if (status === 401 || status === 403) {

                setPreview(null);

                setReceipt(null);

                if (churchId) await clearFinancialState(client, churchId);

            }

            setMessage(axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Unable to complete this action. Please retry.');

        }

        finally {

            setBusy(false);

        }

    }

    function editRow(number: number, update: Partial<ImportRow>) {

        setDirty(true);

        setPreview(p => p ? { ...p, rows: p.rows.map(r => r.row_number === number ? { ...r, ...update } : r) } : null);

    }

    if(setup.isError) return <p role="alert">Unable to load import configuration. Refresh financial access before continuing.</p>;

    return <div className="treasure-imports">

    <div className="treasure-steps" aria-label="Import workflow"><span aria-current={!preview?"step":undefined}>01 · Upload &amp; map</span><span aria-current={preview&&preview.status!=="COMMITTED"?"step":undefined}>02 · Review &amp; reconcile</span><span aria-current={preview?.status==="COMMITTED"?"step":undefined}>03 · Publish &amp; receipt</span></div>

    <p className="treasure-notice">Every row requires an explicit decision. Publishing preserves immutable financial history.</p>

    <p className="treasure-notice">Names and emails are never matched automatically. Donor and fund attribution require explicit review. Manage sources and donor attribution in Settings; create funds in Funds.</p>

    <div className="treasure-import-links"><Link to="/treasure/settings">Manage sources and attribution</Link><Link to="/treasure/funds">Manage funds</Link></div>
    {setup.data && !setup.data.sources.length && <p className="treasure-notice">No import sources yet. Add a source in Settings before uploading your CSV.</p>}
    <p role="status">{message}</p>

    <fieldset disabled={busy}>

      {setup.isPending && <p role="status">Loading import configuration...</p>}

      {setup.isError && <p role="alert">Unable to load import configuration.</p>}

      <section className="treasure-resume"><h2>Resume an import</h2><p>Open an existing preview or committed receipt using its batch reference. This does not upload or publish another batch.</p><label>Resume batch ID <input value={resumeId} onChange={e => setResumeId(e.target.value)}/></label><button disabled={!resumeId.trim()} onClick={() => run(async () => { setDirty(false); setPreviewPage(0); setPreview(await api.preview(resumeId.trim())); })}>Resume preview</button></section>
      <section><h2>Upload CSV</h2><p>Maximum 2 MiB and 5,000 rows. Dates use YYYY-MM-DD. Split gifts require a shared transaction ID and distinct allocation IDs. Values are allocation amounts.</p><label>Source <select value={sourceId} onChange={e => setSourceId(e.target.value)}><option value="">Choose source</option>{setup.data?.sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label><label>CSV file <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] ?? null)}/></label><h3>Map your CSV columns</h3><p>Enter the exact column headers from your file. Optional mappings may be left blank.</p><div className="grid gap-2">{fields.map(f => <label key={f}>{f.replaceAll('_', ' ')} column <input value={mapping[f] ?? ''} onChange={e => setMapping({ ...mapping, [f]: e.target.value })}/></label>)}</div><button disabled={!file || !sourceId} onClick={() => run(async () => { setReceipt(null); setDirty(false); setPreviewPage(0); setPreview(await api.upload(sourceId, file!, Object.fromEntries(Object.entries(mapping).filter(([, v]) => v)))); })}>Create persistent preview</button></section>

      {preview && <section><h2>Review and reconcile</h2><fieldset disabled={preview.status === "COMMITTED"}><p>Batch {preview.id}; version {preview.version}; {preview.status}. Every row needs an explicit decision and review reason.</p><div className="overflow-x-auto"><table><thead><tr>{['Row', 'Date', 'Amount', 'Source fund', 'Errors', 'Donor', 'Fund', 'Decision', 'Review reason'].map(x => <th key={x}>{x}</th>)}</tr></thead><tbody>{preview.rows.slice(previewPage * 50, (previewPage + 1) * 50).map(r => <tr key={r.row_number}><td>{r.row_number}</td><td>{r.values.date}</td><td>{r.values.amount} {r.values.currency}</td><td>{r.values.fund}</td><td>{[...r.errors, r.duplicate_status ?? ''].filter(Boolean).join(', ')}</td><td><select aria-label={`Donor row ${r.row_number}`} value={r.values.donor_id ?? ''} onChange={e => editRow(r.row_number, { values: { ...r.values, donor_id: e.target.value } })}><option value="">Explicitly choose</option>{setup.data?.donors.map(d => <option key={d.id} value={d.id}>{d.display_name} ({d.kind})</option>)}</select></td><td><select aria-label={`Fund row ${r.row_number}`} value={r.values.fund_id ?? ''} onChange={e => editRow(r.row_number, { values: { ...r.values, fund_id: e.target.value } })}><option value="">Choose fund</option>{setup.data?.funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></td><td><select aria-label={`Decision row ${r.row_number}`} value={r.disposition} onChange={e => editRow(r.row_number, { disposition: e.target.value })}>{['REVIEW', 'ACCEPT', 'EXCLUDE', 'DUPLICATE'].map(x => <option key={x}>{x}</option>)}</select></td><td><input aria-label={`Reason row ${r.row_number}`} value={r.reason ?? ''} onChange={e => editRow(r.row_number, { reason: e.target.value })}/></td></tr>)}</tbody></table></div><button disabled={previewPage === 0} onClick={() => setPreviewPage(p => p - 1)}>Previous review rows</button><button disabled={(previewPage + 1) * 50 >= preview.rows.length} onClick={() => setPreviewPage(p => p + 1)}>Next review rows</button><label>Coverage <select value={coverage} onChange={e => { setCoverage(e.target.value); setDirty(true); }}>{['UNKNOWN', 'PARTIAL', 'COMPLETE'].map(x => <option key={x}>{x}</option>)}</select></label><label>Coverage start <input type="date" value={start} onChange={e => { setStart(e.target.value); setDirty(true); }}/></label><label>Coverage end <input type="date" value={end} onChange={e => { setEnd(e.target.value); setDirty(true); }}/></label><button disabled={preview.status === 'COMMITTED'} onClick={() => run(async () => { setPreview(await api.resolve(preview, preview.rows.filter(r => r.disposition !== 'REVIEW').map(r => ({ row_number: r.row_number, disposition: r.disposition, donor_id: r.values.donor_id || null, fund_id: r.values.fund_id || null, reason: r.reason ?? '' })), coverage, start || null, end || null)); setDirty(false); })}>Save reviewed decisions</button><p>{dirty ? "Unsaved decisions: save before publishing." : "Reviewed version ready."}</p><p>Persisted allocation totals: {Object.entries(preview.totals).map(([k, v]) => `${k}: ${v}`).join('; ')}</p><button disabled={dirty || preview.status === 'COMMITTED' || preview.rows.some(r => r.disposition === 'REVIEW')} onClick={() => { if (window.confirm('Publish this reviewed batch? Published financial history is immutable.'))

                void run(async () => { const published = await api.commit(preview); setReceipt(published); setPreview(current => current?.id === preview.id ? { ...current, status: 'COMMITTED', receipt: published } : current); setDirty(false); }); }}>Publish reviewed version</button></fieldset></section>}

      {(receipt ?? preview?.receipt) && <section><h2>Immutable import receipt</h2><p>Batch {(receipt ?? preview?.receipt)?.batch_id}</p><p>Gross {(receipt ?? preview?.receipt)?.gross}; returned {(receipt ?? preview?.receipt)?.returned} {(receipt ?? preview?.receipt)?.currency}</p><p>Coverage: {(receipt ?? preview?.receipt)?.coverage}</p><p>Rows {(receipt ?? preview?.receipt)?.row_count}; duplicates {(receipt ?? preview?.receipt)?.duplicates}; excluded {(receipt ?? preview?.receipt)?.excluded_rows.length}</p><p>Receipt verification: {(receipt ?? preview?.receipt)?.hash}</p></section>}

    </fieldset>

  </div>;

}
